import { Popover } from "radix-ui";
import clsx from "clsx";
import { useRef, useEffect, useState } from "react";

import {
  COLOR_OUTLINE_CONTRAST_THRESHOLD,
  COLOR_PALETTE,
  isColorDark,
} from "@excalidraw/common";

import type { ColorTuple, ColorPaletteCustom } from "@excalidraw/common";

import type {
  ElementBackgroundGradient,
  ExcalidrawElement,
} from "@excalidraw/element/types";

import { useAtom } from "../../editor-jotai";
import { t } from "../../i18n";
import { useExcalidrawContainer, useStylesPanelMode } from "../App";
import { activeEyeDropperAtom } from "../EyeDropper";
import { PropertiesPopover } from "../PropertiesPopover";
import { slashIcon, strokeIcon } from "../icons";
import {
  saveCaretPosition,
  restoreCaretPosition,
  temporarilyDisableTextEditorBlur,
} from "../../hooks/useTextEditorFocus";

import { ColorInput } from "./ColorInput";
import { Spectrum } from "./Spectrum";
import PickerHeading from "./PickerHeading";
import {
  activeColorPickerSectionAtom,
  getColorChipLabel,
} from "./colorPickerUtils";

import "./ColorPicker.scss";
import "./Spectrum.scss";

import type { ColorPickerType } from "./colorPickerUtils";

import type { AppState, UIAppState } from "../../types";

interface ColorPickerProps {
  type: ColorPickerType;
  /**
   * null indicates no color should be displayed as active
   * (e.g. when multiple shapes selected with different colors)
   */
  color: string | null;
  onChange: (color: string) => void;
  label: string;
  elements: readonly ExcalidrawElement[];
  appState: UIAppState;
  palette?: ColorPaletteCustom | null;
  topPicks?: ColorTuple;
  updateData: (formData?: any) => void;
  /** current background gradient (background picker only) */
  gradient?: ElementBackgroundGradient | null;
  /** set/clear the background gradient (enables the gradient tabs) */
  onGradientChange?: (gradient: ElementBackgroundGradient | null) => void;
}

const ColorPickerPopupContent = ({
  type,
  color,
  onChange,
  label,
  palette = COLOR_PALETTE,
  updateData,
  getOpenPopup,
  appState,
  gradient,
  onGradientChange,
}: Pick<
  ColorPickerProps,
  | "type"
  | "color"
  | "onChange"
  | "label"
  | "elements"
  | "palette"
  | "updateData"
  | "appState"
  | "gradient"
  | "onGradientChange"
> & {
  getOpenPopup: () => AppState["openPopup"];
}) => {
  const { container } = useExcalidrawContainer();
  const [, setActiveColorPickerSection] = useAtom(activeColorPickerSectionAtom);

  const [eyeDropperState] = useAtom(activeEyeDropperAtom);

  // let the user drag the popover around the canvas via the header handle
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dragStart = useRef<{
    px: number;
    py: number;
    ox: number;
    oy: number;
  } | null>(null);

  const handleDragPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // don't start a drag from interactive header controls
    if ((event.target as HTMLElement).closest("button, input, select")) {
      return;
    }
    event.preventDefault();
    const el = event.currentTarget;
    el.setPointerCapture(event.pointerId);
    dragStart.current = {
      px: event.clientX,
      py: event.clientY,
      ox: dragOffset.x,
      oy: dragOffset.y,
    };
    const move = (e: PointerEvent) => {
      const start = dragStart.current;
      if (!start) {
        return;
      }
      setDragOffset({
        x: start.ox + e.clientX - start.px,
        y: start.oy + e.clientY - start.py,
      });
    };
    const up = () => {
      dragStart.current = null;
      el.removeEventListener("pointermove", move);
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up, { once: true });
    el.addEventListener("pointercancel", up, { once: true });
  };

  const title =
    type === "elementStroke"
      ? t("labels.stroke")
      : type === "elementBackground"
      ? t("labels.background")
      : t("labels.canvasBackground");

  const colorInputJSX = (
    <div>
      <PickerHeading>{t("colorPicker.hexCode")}</PickerHeading>
      <ColorInput
        color={color || ""}
        label={label}
        onChange={(color) => {
          onChange(color);
        }}
        colorPickerType={type}
        placeholder={t("colorPicker.color")}
      />
    </div>
  );

  return (
    <PropertiesPopover
      container={container}
      // open beside the (right-side) properties panel, and allow dragging
      side="left"
      hideArrow
      style={{
        maxWidth: "15rem",
        transform:
          dragOffset.x || dragOffset.y
            ? `translate(${dragOffset.x}px, ${dragOffset.y}px)`
            : undefined,
      }}
      // Improve focus handling for text editing scenarios
      preventAutoFocusOnTouch={!!appState.editingTextElement}
      onFocusOutside={(event) => {
        // keep the popover open (e.g. during eye-dropper / slider drags)
        // without refocusing the content, which would recurse via focusin
        event.preventDefault();
      }}
      onPointerDownOutside={(event) => {
        if (eyeDropperState) {
          // prevent from closing if we click outside the popover
          // while eyedropping (e.g. click when clicking the sidebar;
          // the eye-dropper-backdrop is prevented downstream)
          event.preventDefault();
        }
      }}
      onClose={() => {
        // only clear if we're still the active popup (avoid racing with switch)
        if (getOpenPopup() === type) {
          updateData({ openPopup: null });
        }
        setActiveColorPickerSection(null);

        // Refocus text editor when popover closes if we were editing text
        if (appState.editingTextElement) {
          setTimeout(() => {
            const textEditor = document.querySelector(
              ".excalidraw-wysiwyg",
            ) as HTMLTextAreaElement;
            if (textEditor) {
              textEditor.focus();
            }
          }, 0);
        }
      }}
    >
      {palette ? (
        <div className="color-picker-content properties-content" tabIndex={-1}>
          <div
            className="spectrum__header"
            onPointerDown={handleDragPointerDown}
          >
            <span className="spectrum__header-title">{title}</span>
          </div>
          <Spectrum
            color={color}
            gradient={gradient}
            onGradientChange={onGradientChange}
            onChange={(changedColor) => {
              // Save caret position before color change if editing text
              const savedSelection = appState.editingTextElement
                ? saveCaretPosition()
                : null;

              onChange(changedColor);

              // Restore caret position after color change if editing text
              if (appState.editingTextElement && savedSelection) {
                restoreCaretPosition(savedSelection);
              }
            }}
          >
            {colorInputJSX}
          </Spectrum>
        </div>
      ) : (
        colorInputJSX
      )}
    </PropertiesPopover>
  );
};

const ColorPickerTrigger = ({
  label,
  color,
  type,
  mode = "background",
  onToggle,
  editingTextElement,
}: {
  color: string | null;
  label: string;
  type: ColorPickerType;
  mode?: "background" | "stroke";
  onToggle: () => void;
  editingTextElement?: boolean;
}) => {
  const stylesPanelMode = useStylesPanelMode();
  const isCompactMode = stylesPanelMode !== "full";
  const isMobileMode = stylesPanelMode === "mobile";
  const handleClick = (e: React.MouseEvent) => {
    // use pointerdown so we run before outside-close logic
    e.preventDefault();
    e.stopPropagation();

    // If editing text, temporarily disable the wysiwyg blur event
    if (editingTextElement) {
      temporarilyDisableTextEditorBlur();
    }

    onToggle();
  };

  const title =
    type === "elementStroke"
      ? t("labels.showStroke")
      : t("labels.showBackground");

  if (!isCompactMode) {
    const isTransparentColor = !color || color === "transparent";
    const hasOutline =
      !color || !isColorDark(color, COLOR_OUTLINE_CONTRAST_THRESHOLD);

    return (
      <Popover.Trigger
        type="button"
        className="color-picker__chip properties-trigger"
        aria-label={label}
        title={title}
        data-openpopup={type}
        onClick={handleClick}
      >
        <span
          className={clsx("color-picker__button color-picker__chip-swatch", {
            "is-transparent": isTransparentColor,
            "has-outline": hasOutline,
          })}
          style={color ? { "--swatch-color": color } : undefined}
        >
          <div className="color-picker__button-outline">
            {!color && slashIcon}
          </div>
        </span>
        <span className="color-picker__chip-label">
          {getColorChipLabel(color)}
        </span>
      </Popover.Trigger>
    );
  }

  return (
    <Popover.Trigger
      type="button"
      className={clsx("color-picker__button active-color properties-trigger", {
        "is-transparent": !color || color === "transparent",
        "has-outline":
          !color || !isColorDark(color, COLOR_OUTLINE_CONTRAST_THRESHOLD),
        "compact-sizing": isCompactMode,
        "mobile-border": isMobileMode,
      })}
      aria-label={label}
      style={color ? { "--swatch-color": color } : undefined}
      title={title}
      data-openpopup={type}
      onClick={handleClick}
    >
      <div className="color-picker__button-outline">{!color && slashIcon}</div>
      {isCompactMode && color && mode === "stroke" && (
        <div className="color-picker__button-background">
          <span
            style={{
              color:
                color && isColorDark(color, COLOR_OUTLINE_CONTRAST_THRESHOLD)
                  ? "#fff"
                  : "#111",
            }}
          >
            {strokeIcon}
          </span>
        </div>
      )}
    </Popover.Trigger>
  );
};

export const ColorPicker = ({
  type,
  color,
  onChange,
  label,
  elements,
  palette = COLOR_PALETTE,
  topPicks,
  updateData,
  appState,
  gradient,
  onGradientChange,
}: ColorPickerProps) => {
  const openRef = useRef(appState.openPopup);
  useEffect(() => {
    openRef.current = appState.openPopup;
  }, [appState.openPopup]);

  // this picker can be mounted more than once (e.g. a visible right-side panel
  // plus a hidden legacy panel, or a hidden inactive sidebar tab). Only a
  // currently-visible mount should ever open its popover, otherwise a hidden
  // one anchors a duplicate popover at the top-left corner. Checked live at
  // click-time (not cached) so it stays correct across tab switches, panel
  // mode changes, etc. that don't otherwise re-run an effect.
  const containerRef = useRef<HTMLDivElement>(null);
  const isVisible = () => containerRef.current?.offsetParent != null;

  return (
    <div ref={containerRef}>
      <div role="dialog" aria-modal="true" className="color-picker-container">
        <Popover.Root
          open={appState.openPopup === type && isVisible()}
          onOpenChange={(open) => {
            if (open && isVisible()) {
              updateData({ openPopup: type });
            }
          }}
        >
          {/* serves as an active color indicator as well */}
          <ColorPickerTrigger
            color={color}
            label={label}
            type={type}
            mode={type === "elementStroke" ? "stroke" : "background"}
            editingTextElement={!!appState.editingTextElement}
            onToggle={() => {
              if (!isVisible()) {
                return;
              }
              // atomic switch: if another popup is open, close it first, then open this one next tick
              if (appState.openPopup === type) {
                // toggle off on same trigger
                updateData({ openPopup: null });
              } else if (appState.openPopup) {
                updateData({ openPopup: type });
              } else {
                // open this one
                updateData({ openPopup: type });
              }
            }}
          />
          {/* popup content */}
          {appState.openPopup === type && (
            <ColorPickerPopupContent
              type={type}
              color={color}
              onChange={onChange}
              label={label}
              elements={elements}
              palette={palette}
              updateData={updateData}
              getOpenPopup={() => openRef.current}
              appState={appState}
              gradient={gradient}
              onGradientChange={onGradientChange}
            />
          )}
        </Popover.Root>
      </div>
    </div>
  );
};
